import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
    UserCircle, Trash, Prohibit, ShieldCheck, MagnifyingGlass, Crown, ChatCircle, ChatText,
    Bug, Lightbulb, Heart, ChatCircleDots, Star, CheckCircle, MapPin, ArrowSquareOut,
} from "@phosphor-icons/react";

function Stat({ label, value }) {
    return (
        <div className="bg-white border border-stone-200 rounded-xl p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-1.5">{label}</div>
            <div className="font-heading text-2xl font-bold text-[#0A0A0A]">{value}</div>
        </div>
    );
}

function UsersTab() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = q ? { q } : {};
            const { data } = await api.get("/admin/users", { params });
            setUsers(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-line */ }, []);

    const toggleBan = async (u) => {
        try {
            const { data } = await api.post(`/admin/users/${u.id}/ban`);
            setUsers((cur) => cur.map((x) => x.id === u.id ? { ...x, is_banned: data.is_banned } : x));
            toast.success(data.is_banned ? "User suspended" : "User reinstated");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not update");
        }
    };

    const setRole = async (u, role) => {
        if (role === u.role) return;
        try {
            await api.post(`/admin/users/${u.id}/role`, { role });
            setUsers((cur) => cur.map((x) => x.id === u.id ? { ...x, role } : x));
            toast.success(`Role updated to ${role}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not update role");
        }
    };

    const del = async (u) => {
        if (!window.confirm(`Permanently delete ${u.name} and all their content?`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            setUsers((cur) => cur.filter((x) => x.id !== u.id));
            toast.success("User deleted");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not delete");
        }
    };

    return (
        <div>
            <form onSubmit={(e) => { e.preventDefault(); load(); }} className="bg-white border border-stone-200 rounded-xl p-3 mb-4 flex items-center gap-2">
                <MagnifyingGlass size={16} className="text-stone-400 ml-2" />
                <input
                    data-testid="admin-users-search-input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by email or name…"
                    className="flex-1 bg-transparent outline-none text-sm placeholder:text-stone-400"
                />
                <Button type="submit" data-testid="admin-users-search-btn" className="h-9 bg-[#990000] hover:bg-[#7A0000] text-white text-sm">Search</Button>
            </form>
            <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-stone-50 border-b border-stone-200">
                        <tr>
                            <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-stone-500 font-bold">User</th>
                            <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-stone-500 font-bold">Role</th>
                            <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-stone-500 font-bold">Posts</th>
                            <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-stone-500 font-bold">Status</th>
                            <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-stone-500 font-bold">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="px-4 py-6 text-center text-stone-400 text-sm">Loading…</td></tr>
                        ) : users.length === 0 ? (
                            <tr><td colSpan={5} className="px-4 py-6 text-center text-stone-400 text-sm">No users found</td></tr>
                        ) : users.map((u) => (
                            <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-stone-100 hover:bg-stone-50">
                                <td className="px-4 py-3">
                                    <Link to={`/users/${u.id}`} className="flex items-center gap-3 hover:text-[#990000]">
                                        <div className="w-8 h-8 rounded-full bg-[#990000] text-[#FFCC00] flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                                            {u.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-medium text-stone-800 truncate flex items-center gap-1.5">
                                                {u.name}
                                                {u.email_verified && <ShieldCheck size={12} className="text-[#16A34A]" weight="fill" />}
                                            </div>
                                            <div className="text-xs text-stone-500 truncate">{u.email}</div>
                                        </div>
                                    </Link>
                                </td>
                                <td className="px-4 py-3">
                                    <Select value={u.role} onValueChange={(v) => setRole(u, v)} disabled={u.id === me?.id}>
                                        <SelectTrigger data-testid={`admin-role-select-${u.id}`} className="h-8 w-28 text-xs border-stone-200">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="user">User</SelectItem>
                                            <SelectItem value="admin"><span className="inline-flex items-center gap-1"><Crown size={12} weight="fill" className="text-[#FFCC00]" /> Admin</span></SelectItem>
                                        </SelectContent>
                                    </Select>
                                </td>
                                <td className="px-4 py-3 text-stone-700">{u.posts_count}</td>
                                <td className="px-4 py-3">
                                    {u.is_banned ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#990000]/10 text-[#990000] text-[10px] uppercase tracking-wider font-bold">
                                            <Prohibit size={10} weight="bold" /> Banned
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 text-[10px] uppercase tracking-wider font-bold">
                                            Active
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex justify-end gap-2">
                                        <Button
                                            data-testid={`admin-ban-btn-${u.id}`}
                                            onClick={() => toggleBan(u)}
                                            disabled={u.id === me?.id}
                                            variant="outline"
                                            className="h-8 px-3 text-xs border-stone-200"
                                        >
                                            {u.is_banned ? "Unban" : "Ban"}
                                        </Button>
                                        <Button
                                            data-testid={`admin-delete-user-btn-${u.id}`}
                                            onClick={() => del(u)}
                                            disabled={u.id === me?.id}
                                            variant="outline"
                                            className="h-8 px-3 text-xs border-[#990000]/30 text-[#990000] hover:bg-[#990000]/5 hover:text-[#990000]"
                                        >
                                            <Trash size={12} />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function CommentsTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/recent-comments");
            setItems(data);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const del = async (c) => {
        if (!window.confirm("Delete this comment?")) return;
        try {
            await api.delete(`/admin/comments/${c.id}`);
            setItems((cur) => cur.filter((x) => x.id !== c.id));
            toast.success("Comment deleted");
        } catch (_) { toast.error("Could not delete"); }
    };

    if (loading) return <div className="text-stone-400 text-sm">Loading…</div>;
    if (items.length === 0) return <div className="text-stone-400 text-sm">No comments yet</div>;

    return (
        <div className="space-y-3">
            {items.map((c) => (
                <div key={c.id} data-testid={`admin-comment-${c.id}`} className="bg-white border border-stone-200 rounded-xl p-4 flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#990000] text-[#FFCC00] flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                        {(c.user_name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-xs text-stone-500 mb-1">
                            <Link to={`/users/${c.user_id}`} className="font-medium text-stone-700 hover:text-[#990000]">{c.user_name}</Link>
                            <span>·</span>
                            <Link to={`/posts/${c.post_id}`} className="hover:text-[#990000]">View thread</Link>
                            <span>·</span>
                            <span>{new Date(c.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-sm text-stone-700 leading-relaxed line-clamp-3">{c.content}</p>
                    </div>
                    <Button
                        data-testid={`admin-delete-comment-btn-${c.id}`}
                        onClick={() => del(c)}
                        variant="outline"
                        className="h-8 px-3 text-xs border-[#990000]/30 text-[#990000] hover:bg-[#990000]/5 hover:text-[#990000] flex-shrink-0"
                    >
                        <Trash size={12} />
                    </Button>
                </div>
            ))}
        </div>
    );
}

function PostsTab() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (cat !== "all") params.category = cat;
            const { data } = await api.get("/admin/posts", { params });
            setPosts(data);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-line */ }, [cat]);

    const del = async (p) => {
        if (!window.confirm(`Delete "${p.title}"? This removes its comments, upvotes, and bookmarks.`)) return;
        try {
            await api.delete(`/admin/posts/${p.id}`);
            setPosts((cur) => cur.filter((x) => x.id !== p.id));
            toast.success("Post deleted");
        } catch (_) { toast.error("Could not delete"); }
    };

    const CAT_LABEL = { flight: "Flight", hotel: "Hotel", destination: "Destination", activity: "Activity" };

    return (
        <div data-testid="admin-posts-tab">
            <form
                onSubmit={(e) => { e.preventDefault(); load(); }}
                className="bg-white border border-stone-200 rounded-xl p-3 mb-4 flex items-center gap-2 flex-wrap"
            >
                <div className="flex items-center gap-2 flex-1 min-w-[180px] border border-stone-200 rounded-lg px-3 h-9">
                    <MagnifyingGlass size={14} className="text-stone-400" />
                    <input
                        data-testid="admin-posts-search-input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search title, city, author…"
                        className="flex-1 bg-transparent outline-none text-sm placeholder:text-stone-400"
                    />
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                    {["all", "destination", "flight", "hotel", "activity"].map((c) => (
                        <button
                            key={c}
                            type="button"
                            data-testid={`admin-posts-filter-${c}`}
                            onClick={() => setCat(c)}
                            className={`px-3 h-8 rounded-md text-xs font-medium uppercase tracking-wider transition-colors ${
                                cat === c
                                    ? "bg-[#990000] text-white"
                                    : "text-stone-600 hover:bg-stone-100"
                            }`}
                        >
                            {c === "all" ? "All" : CAT_LABEL[c]}
                        </button>
                    ))}
                </div>
                <Button type="submit" data-testid="admin-posts-search-btn" className="h-9 bg-[#990000] hover:bg-[#7A0000] text-white text-sm">Search</Button>
            </form>

            {loading ? (
                <div className="text-stone-400 text-sm">Loading…</div>
            ) : posts.length === 0 ? (
                <div className="bg-white border border-stone-200 rounded-xl p-12 text-center text-stone-500 text-sm">No posts</div>
            ) : (
                <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 border-b border-stone-200">
                            <tr>
                                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-stone-500 font-bold">Post</th>
                                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-stone-500 font-bold">Category</th>
                                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-stone-500 font-bold">Author</th>
                                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-stone-500 font-bold">Engagement</th>
                                <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-stone-500 font-bold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {posts.map((p) => (
                                <tr key={p.id} data-testid={`admin-post-row-${p.id}`} className="border-t border-stone-100 hover:bg-stone-50">
                                    <td className="px-4 py-3 max-w-md">
                                        <Link to={`/posts/${p.id}`} className="hover:text-[#990000]">
                                            <div className="font-medium text-stone-800 truncate">{p.title}</div>
                                            <div className="text-xs text-stone-500 inline-flex items-center gap-1 mt-0.5">
                                                <MapPin size={10} /> {p.city}
                                            </div>
                                        </Link>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#FFF4CC] border border-[#FFE680] text-[#5C4400] text-[10px] uppercase tracking-wider font-bold">
                                            {CAT_LABEL[p.category] || p.category}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <Link to={`/users/${p.user_id}`} className="text-stone-700 hover:text-[#990000]">{p.user_name}</Link>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-stone-600">
                                        <div>{p.upvotes} upvotes</div>
                                        <div>{p.comments_count} comments</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-end gap-2">
                                            <Link
                                                to={`/posts/${p.id}`}
                                                data-testid={`admin-view-post-btn-${p.id}`}
                                                className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-stone-200 hover:border-[#990000] hover:text-[#990000] text-stone-500"
                                                title="View post"
                                            >
                                                <ArrowSquareOut size={12} />
                                            </Link>
                                            <Button
                                                data-testid={`admin-delete-post-btn-${p.id}`}
                                                onClick={() => del(p)}
                                                variant="outline"
                                                className="h-8 px-3 text-xs border-[#990000]/30 text-[#990000] hover:bg-[#990000]/5 hover:text-[#990000]"
                                            >
                                                <Trash size={12} />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function FeedbackTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");

    const CAT_META = {
        bug: { Icon: Bug, label: "Bug", color: "text-[#990000] bg-[#990000]/5 border-[#990000]/20" },
        feature: { Icon: Lightbulb, label: "Feature", color: "text-[#5C4400] bg-[#FFF4CC] border-[#FFE680]" },
        general: { Icon: ChatCircleDots, label: "General", color: "text-stone-700 bg-stone-100 border-stone-200" },
        praise: { Icon: Heart, label: "Praise", color: "text-[#16A34A] bg-[#16A34A]/5 border-[#16A34A]/20" },
    };
    const STATUS_META = {
        new: { color: "bg-[#990000] text-[#FFCC00]", label: "New" },
        reviewed: { color: "bg-stone-200 text-stone-700", label: "Reviewed" },
        resolved: { color: "bg-[#16A34A]/15 text-[#16A34A]", label: "Resolved" },
    };

    const load = async () => {
        setLoading(true);
        try {
            const params = statusFilter === "all" ? {} : { status: statusFilter };
            const { data } = await api.get("/admin/feedback", { params });
            setItems(data);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-line */ }, [statusFilter]);

    const setStatus = async (f, status) => {
        try {
            const { data } = await api.patch(`/admin/feedback/${f.id}`, { status });
            setItems((cur) => cur.map((x) => x.id === f.id ? { ...x, status: data.status } : x));
            toast.success(`Marked ${data.status}`);
        } catch (_) { toast.error("Could not update"); }
    };

    const del = async (f) => {
        if (!window.confirm("Delete this feedback?")) return;
        try {
            await api.delete(`/admin/feedback/${f.id}`);
            setItems((cur) => cur.filter((x) => x.id !== f.id));
            toast.success("Feedback deleted");
        } catch (_) { toast.error("Could not delete"); }
    };

    return (
        <div data-testid="admin-feedback-tab">
            <div className="bg-white border border-stone-200 rounded-xl p-3 mb-4 flex items-center gap-2 flex-wrap">
                <span className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500 ml-1">Status:</span>
                {["all", "new", "reviewed", "resolved"].map((s) => (
                    <button
                        key={s}
                        data-testid={`admin-feedback-filter-${s}`}
                        onClick={() => setStatusFilter(s)}
                        className={`px-3 h-8 rounded-md text-xs font-medium uppercase tracking-wider transition-colors ${
                            statusFilter === s
                                ? "bg-[#990000] text-white"
                                : "text-stone-600 hover:bg-stone-100"
                        }`}
                    >
                        {s}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-stone-400 text-sm">Loading…</div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-stone-200 rounded-xl p-12 text-center">
                    <ChatText size={32} className="mx-auto text-stone-300 mb-2" weight="regular" />
                    <div className="text-sm text-stone-500">No feedback in this view yet.</div>
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((f) => {
                        const cat = CAT_META[f.category] || CAT_META.general;
                        const stm = STATUS_META[f.status] || STATUS_META.new;
                        const CatIcon = cat.Icon;
                        return (
                            <div
                                key={f.id}
                                data-testid={`admin-feedback-${f.id}`}
                                className="bg-white border border-stone-200 rounded-xl p-5"
                            >
                                <div className="flex items-center gap-2 flex-wrap mb-3">
                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider border ${cat.color}`}>
                                        <CatIcon size={11} weight="fill" />
                                        {cat.label}
                                    </span>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${stm.color}`}>
                                        {stm.label}
                                    </span>
                                    <div className="inline-flex items-center gap-0.5 ml-auto">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <Star
                                                key={n}
                                                size={12}
                                                weight={n <= f.rating ? "fill" : "regular"}
                                                className={n <= f.rating ? "text-[#FFCC00]" : "text-stone-300"}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-line mb-4">
                                    {f.message}
                                </p>

                                <div className="flex items-center justify-between flex-wrap gap-3 pt-3 border-t border-stone-100 text-xs">
                                    <div className="flex items-center gap-2 text-stone-500">
                                        <Link to={`/users/${f.user_id}`} className="font-medium text-stone-700 hover:text-[#990000]">
                                            {f.user_name}
                                        </Link>
                                        <span>·</span>
                                        <span className="font-mono">{f.user_email}</span>
                                        <span>·</span>
                                        <span>{new Date(f.created_at).toLocaleString()}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {f.status !== "reviewed" && (
                                            <Button
                                                data-testid={`admin-feedback-mark-reviewed-${f.id}`}
                                                onClick={() => setStatus(f, "reviewed")}
                                                variant="outline"
                                                className="h-8 px-3 text-xs border-stone-200"
                                            >
                                                Mark reviewed
                                            </Button>
                                        )}
                                        {f.status !== "resolved" && (
                                            <Button
                                                data-testid={`admin-feedback-mark-resolved-${f.id}`}
                                                onClick={() => setStatus(f, "resolved")}
                                                className="h-8 px-3 text-xs bg-[#16A34A] hover:bg-[#15803D] text-white"
                                            >
                                                <CheckCircle size={12} weight="bold" className="mr-1" />
                                                Resolve
                                            </Button>
                                        )}
                                        <Button
                                            data-testid={`admin-feedback-delete-${f.id}`}
                                            onClick={() => del(f)}
                                            variant="outline"
                                            className="h-8 px-3 text-xs border-[#990000]/30 text-[#990000] hover:bg-[#990000]/5 hover:text-[#990000]"
                                        >
                                            <Trash size={12} />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default function AdminPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    }, []);

    if (user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
                <div className="text-stone-500 text-sm">Loading…</div>
            </div>
        );
    }
    if (!user || user.role !== "admin") return <Navigate to="/feed" replace />;

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-page">
            <Navbar />

            <header className="bg-gradient-to-br from-[#990000] to-[#7A0000] relative overflow-hidden">
                <div className="absolute -right-12 -top-12 w-72 h-72 rounded-full bg-[#FFCC00]/10 blur-3xl" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative">
                    <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFCC00] mb-2 inline-flex items-center gap-1.5">
                        <Crown size={12} weight="fill" /> Admin Console
                    </div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tighter">
                        Manage TrojanTrails
                    </h1>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
                        <Stat label="Users" value={stats.total_users} />
                        <Stat label="Verified" value={stats.verified_users} />
                        <Stat label="Banned" value={stats.banned_users} />
                        <Stat label="Posts" value={stats.total_posts} />
                        <Stat label="Comments" value={stats.total_comments} />
                        <Stat label="Trip Plans" value={stats.total_collections} />
                        <Stat label="Feedback" value={stats.total_feedback || 0} />
                        <Stat label="New FB" value={stats.new_feedback || 0} />
                    </div>
                )}

                <Tabs defaultValue="users">
                    <TabsList className="bg-white border border-stone-200 mb-6 flex-wrap h-auto">
                        <TabsTrigger value="users" data-testid="admin-tab-users" className="data-[state=active]:bg-[#990000] data-[state=active]:text-white">
                            <UserCircle size={14} className="mr-1.5" /> Users
                        </TabsTrigger>
                        <TabsTrigger value="posts" data-testid="admin-tab-posts" className="data-[state=active]:bg-[#990000] data-[state=active]:text-white">
                            <MapPin size={14} className="mr-1.5" /> Posts
                        </TabsTrigger>
                        <TabsTrigger value="comments" data-testid="admin-tab-comments" className="data-[state=active]:bg-[#990000] data-[state=active]:text-white">
                            <ChatCircle size={14} className="mr-1.5" /> Comments
                        </TabsTrigger>
                        <TabsTrigger value="feedback" data-testid="admin-tab-feedback" className="data-[state=active]:bg-[#990000] data-[state=active]:text-white">
                            <ChatText size={14} className="mr-1.5" /> Feedback
                            {stats?.new_feedback > 0 && (
                                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-[#FFCC00] text-[#5C0000] text-[10px] font-bold px-1">
                                    {stats.new_feedback}
                                </span>
                            )}
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="users"><UsersTab /></TabsContent>
                    <TabsContent value="posts"><PostsTab /></TabsContent>
                    <TabsContent value="comments"><CommentsTab /></TabsContent>
                    <TabsContent value="feedback"><FeedbackTab /></TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
